import React, { useCallback, useContext, useEffect } from 'react';
import { Route, RouteComponentProps, useLocation } from 'react-router';
import { Redirect, Switch } from 'react-router-dom';
import cx from 'classnames';
import { AuthAddressContext } from 'Context/Context';
import useAddress from 'hooks/use-address';
import useAsignaProvider from 'hooks/use-asigna-provider';
import { useBreakpoints } from 'hooks/use-breakpoints';
import useNetwork from 'hooks/use-network';
import useData, { PROVIDERS } from 'hooks/useData';
import { useWalletProvider } from 'hooks/useWalletProvider';
import { isNull } from 'lodash';
import {
  ActivityPage,
  AppsPage,
  AssetsPage,
  ConnectWalletPage,
  ContactsPage,
  CreateSafePage,
  DashboardPage,
  OwnersPage,
  PendingSafeDetailsPage,
  ProPage,
  SettingsPage,
  StackingPage,
  TreasuryPage,
  UnsubscribePage,
} from 'pages';
import { authenticateExtension, getConnectedInfo } from 'services/safeExtensionService';
import { Toaster } from 'sonner';
import { useExtensionState, useTokenStorage } from 'store';
import { getStorageKeysForNetwork } from 'utils/LocalstorageUtils';

import { RouteGuard } from './components/RouteGuard';
import { useLayout } from './util/useLayout';
import { Sidebar } from './components';

const { getToken } = useTokenStorage();

// TODO_R: переработать точно также как по аналогу в btc, декомпозировав на компоненты. Пример в bitcoin-multisig - projects/frontend/bitcoin-multisig/src/modules/layout/RootLayout/index.tsx
// TODO_R: [NBOS] вытащить в shared projects одинаковые компоненты

interface RootLayoutProps extends RouteComponentProps {}

export const RootLayout: React.FC<RootLayoutProps> = ({ match }) => {
  const location = useLocation();
  const domain = match.url;
  const walletAccount = useAddress();
  const { onMobileZone } = useBreakpoints();

  const { signMessage } = useData();
  const { getContacts } = useData();
  const [network] = useNetwork();
  const authContextData = useContext(AuthAddressContext);
  const walletProvider = useWalletProvider();

  useEffect(() => {
    if (!authContextData) return;
    if (
      (!authContextData[network] || !getToken()) &&
      localStorage.getItem(getStorageKeysForNetwork().walletType)
    ) {
      if (walletProvider) signMessage(walletProvider.provider as PROVIDERS);
    }
  }, [authContextData]);

  useEffect(
    function syncContacts() {
      if (walletAccount) getContacts();
      // TODO: add timer autoupdate
    },
    [walletAccount, network]
  );

  const isNotAuthenticated = !walletAccount && !isNull(walletAccount);

  const { isHeaderHidden, setHeaderHidden } = useLayout();

  useEffect(() => {
    const pathWithHeader = ['/createSafe', '/', '/connect'];
    if (pathWithHeader.includes(location.pathname)) {
      setHeaderHidden(false);
    } else {
      setHeaderHidden(true);
    }
  }, [location, isNotAuthenticated]);

  const getPath = (path: string) => [
    `${domain}vault/:safeAddress/${path}`,
    `${domain}multisig/:safeId/${path}`,
  ];
  const { setExtensionConnected } = useExtensionState();

  const authExt = useCallback(
    (isSilent?: boolean) => {
      const token = getToken() || '';
      const storageKeys = getStorageKeysForNetwork();
      const mainnetAddress = localStorage.getItem(storageKeys.mainnetAddress);
      const testnetAddress = localStorage.getItem(storageKeys.testnetAddress) || '';
      authenticateExtension({
        token,
        address: mainnetAddress || '',
        testnetAddress,
        network,
        isSilent,
      });
      setExtensionConnected(true);
    },
    [walletAccount, network]
  );

  const provider = useAsignaProvider();

  useEffect(() => {
    if (!walletAccount || !provider) return;
    getConnectedInfo().then((res) => {
      if (!res.result.token) return;
      authExt(true);
    });
  }, [network, walletAccount, authExt, provider]);

  return (
    <div
      className={cx(
        'bg-black-0 h-screen',
        onMobileZone || !isHeaderHidden ? 'flex flex-col' : 'flex'
      )}
    >
      <Sidebar isSidebarDisplayed={isHeaderHidden} />

      {/* TODO_R: переделать в компонент */}
      {/* {getToastContainer()} */}
      <Switch>
        <Route exact strict path={`${domain}unsubscribe`} render={() => <UnsubscribePage />} />
        <Route
          exact
          strict
          path="/"
          render={() => (isNotAuthenticated ? <ConnectWalletPage /> : <TreasuryPage />)}
        />
        <Route
          exact
          strict
          path={`${match.url}createSafe`}
          render={() => (isNotAuthenticated ? <Redirect to="/" /> : <CreateSafePage />)}
        />
        <Route
          exact
          strict
          path={`${match.url}invite/:safeId`}
          render={() => (isNotAuthenticated ? <Redirect to="/" /> : <PendingSafeDetailsPage />)}
        />
        <Route
          exact
          strict
          path={getPath('contacts')}
          render={() => (
            <RouteGuard>
              <ContactsPage />
            </RouteGuard>
          )}
        />
        <Route
          exact
          strict
          path={getPath('assets')}
          render={() => (
            <RouteGuard>
              <AssetsPage />
            </RouteGuard>
          )}
        />
        <Route
          exact
          strict
          path={getPath('apps')}
          render={() => (
            <RouteGuard>
              <AppsPage />
            </RouteGuard>
          )}
        />
        <Route
          exact
          strict
          path={getPath('activity')}
          render={() => (
            <RouteGuard>
              <ActivityPage />
            </RouteGuard>
          )}
        />
        <Route
          exact
          strict
          path={getPath('permissions')}
          render={() => (
            <RouteGuard>
              <OwnersPage />
            </RouteGuard>
          )}
        />
        <Route
          exact
          strict
          path={getPath('settings')}
          render={() => (
            <RouteGuard>
              <SettingsPage />
            </RouteGuard>
          )}
        />
        <Route
          exact
          strict
          path={getPath('dashboard')}
          render={() => (
            <RouteGuard>
              <DashboardPage />
            </RouteGuard>
          )}
        />
        <Route
          exact
          strict
          path={getPath('pro')}
          render={() => (
            <RouteGuard>
              <ProPage />
            </RouteGuard>
          )}
        />
        <Route exact strict path={getPath('stacking')} render={() => <StackingPage />} />
        <Route
          exact
          strict
          path={getPath('sbtc')}
          render={() => <StackingPage url="https://old.sbtc.stacks.co/" />}
        />
      </Switch>
      <Toaster
        expand
        // closeButton
        offset="24px"
        toastOptions={{
          duration: 5000,
          unstyled: true,
          classNames: {
            error: 'border-error bg-error-3',
            success: 'border-success bg-success-3',
            warning: 'border-warning bg-warning-3',
            info: 'border-primary bg-primary-3',
          },
        }}
      />
    </div>
  );
};
